'use client';

// Single-file upload control with drag-and-drop + click-to-browse + preview.
// Pure presentation: it surfaces the picked File via onChange/onUpload but
// doesn't talk to any storage backend itself — the consumer wires that to
// Payload's Media collection (or an Express endpoint).
//
// Pair with FormField for label/error semantics; this is the inner widget.

import { useCallback, useId, useRef, useState, type DragEvent } from 'react';
import { cn } from '../lib/cn';

export interface MediaUploaderProps {
  /** Currently-uploaded asset shown as preview. URL is rendered as <img>. */
  value?: { url: string; name?: string } | null;
  /** Fires on every file pick — debounce/upload at the parent level. */
  onChange?: (file: File | null) => void;
  /** Convenience: fires once after onChange when a file is picked. */
  onUpload?: (file: File) => void;
  /** MIME types accept attribute. Defaults to images. */
  accept?: string;
  /** Soft size cap in bytes — files over this trigger onError instead of onChange. */
  maxSize?: number;
  onError?: (message: string) => void;
  /** True while parent is uploading the picked file. */
  uploading?: boolean;
  /** Disables drop + click. */
  disabled?: boolean;
  hasError?: boolean;
  className?: string;
  id?: string;
  'aria-describedby'?: string;
}

function bytesToHuman(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

export function MediaUploader({
  value,
  onChange,
  onUpload,
  accept = 'image/*',
  maxSize,
  onError,
  uploading,
  disabled,
  hasError,
  className,
  id,
  'aria-describedby': ariaDescribedBy,
}: MediaUploaderProps) {
  const reactId = useId();
  const inputId = id ?? `uploader-${reactId}`;
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const accept_ = accept;

  const handleFiles = useCallback(
    (fileList: FileList | null) => {
      const file = fileList?.[0];
      if (!file) {
        onChange?.(null);
        return;
      }
      if (maxSize && file.size > maxSize) {
        onError?.(`File too large. Max ${bytesToHuman(maxSize)}.`);
        return;
      }
      onChange?.(file);
      onUpload?.(file);
    },
    [maxSize, onChange, onError, onUpload],
  );

  function onClick() {
    if (disabled || uploading) return;
    inputRef.current?.click();
  }
  function onDragOver(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    if (disabled || uploading) return;
    setIsDragging(true);
  }
  function onDragLeave() {
    setIsDragging(false);
  }
  function onDrop(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setIsDragging(false);
    if (disabled || uploading) return;
    handleFiles(e.dataTransfer.files);
  }

  return (
    <div
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-disabled={disabled || undefined}
      aria-describedby={ariaDescribedBy}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      className={cn(
        'flex min-h-[140px] cursor-pointer flex-col items-center justify-center gap-2 rounded border-2 border-dashed p-4 text-sm transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-1',
        isDragging ? 'border-brand-500 bg-brand-50' : 'border-gray-300 bg-gray-50 hover:bg-gray-100',
        disabled && 'pointer-events-none opacity-50',
        hasError && 'border-red-500',
        className,
      )}
    >
      <input
        ref={inputRef}
        id={inputId}
        type="file"
        accept={accept_}
        disabled={disabled || uploading}
        className="sr-only"
        onChange={(e) => handleFiles(e.target.files)}
      />
      {value ? (
        <div className="flex flex-col items-center gap-1">
          {/* Plain <img> so consumers don't need next/image config in Storybook */}
          <img
            src={value.url}
            alt={value.name ?? 'Uploaded preview'}
            className="max-h-32 rounded object-cover"
          />
          <span className="text-xs text-gray-500">{value.name}</span>
          <span className="text-xs text-brand-700">Click or drop to replace</span>
        </div>
      ) : uploading ? (
        <span className="text-gray-600">Uploading…</span>
      ) : (
        <>
          <span className="font-medium text-gray-700">Drop a file or click to browse</span>
          <span className="text-xs text-gray-500">
            {accept_ === 'image/*' ? 'PNG, JPG, WebP' : accept_}
            {maxSize ? ` · up to ${bytesToHuman(maxSize)}` : ''}
          </span>
        </>
      )}
    </div>
  );
}
